"use client";

import React from "react";

type StarBorderProps<T extends React.ElementType> =
  React.ComponentPropsWithoutRef<T> & {
    as?: T;
    className?: string;
    children?: React.ReactNode;
    color?: string;
    speed?: React.CSSProperties["animationDuration"];
    thickness?: number;
  };

const StarBorder = <T extends React.ElementType = "button">({
  as,
  className = "",
  color = "white",
  speed = "7s",
  thickness = 2,
  children,
  ...rest
}: StarBorderProps<T>) => {
  const Component = as || "button";

  const resolveColorToken = (token: string): string => {
    // Mappe les jetons de couleur du thème Tailwind vers des valeurs CSS runtime
    const map: Record<string, string> = {
      primary: "#C4992D",
      black: "#000000",
      white: "#FFFFFF",
      // Ajoutez ici d'autres alias si nécessaire (ex: "gold-500": "#C4992D")
    };
    return map[token] ?? token;
  };

  const resolvedColor = resolveColorToken(color);

  return (
    <Component
      className={`relative inline-block overflow-hidden rounded-2xl ${className}`}
      {...(rest as any)}
      style={{
        padding: `${thickness}px 0`,
        ...(rest as any).style,
      }}
    >
      <div
        className="absolute bottom-[-11px] right-[-250%] z-0 h-[50%] w-[300%] animate-star-movement-bottom rounded-full opacity-70"
        style={{
          background: `radial-gradient(circle, ${resolvedColor}, transparent 10%)`,
          animationDuration: speed,
        }}
      ></div>
      <div
        className="absolute left-[-250%] top-[-10px] z-0 h-[50%] w-[300%] animate-star-movement-top rounded-full opacity-70"
        style={{
          background: `radial-gradient(circle, ${resolvedColor}, transparent 10%)`,
          animationDuration: speed,
        }}
      ></div>
      <div className="z-1 relative rounded-2xl border border-neutral-500 bg-gradient-to-b from-black/90 to-black/60 px-[26px] py-[16px] text-center text-[16px] text-white">
        {children}
      </div>
    </Component>
  );
};

export default StarBorder;
